import { Ban, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  canDemote,
  canKick,
  canPromote,
  hasAnyModerationAction,
} from '../moderation';
import { PresenceDot, usePresence } from '../presence';
import { useToasts } from '../toasts';
import type {
  AvatarInspection,
  BuildingInspection,
  InspectionData,
  PlayerId,
  PropInspection,
  TileInspection,
} from '../wire';
import { useLiveWire } from '../wire';
import { tileIndex, useWorldModel } from '../world';
import { materialLabel, biomeLabel, elevationBand, cropStageLabel, freshnessLabel, moistureLabel, windDirectionLabel } from '../data/labels';
import { SPOILAGE_STAGES } from '../pixi/palette';
import { propLabel } from '../data/prop-catalog';
import { useInspection } from './store';
import styles from './inspect.module.css';

/**
 * Inspection panel — modal-like overlay that renders a body variant per
 * the active `InspectionData.kind`. Clicks outside the panel + Escape
 * close it. The panel is non-blocking: the canvas beneath continues to
 * receive camera input (pan/zoom).
 */
export function InspectionPanel(): JSX.Element | null {
  const target = useInspection((s) => s.target);
  const data = useInspection((s) => s.data);
  const awaitingServer = useInspection((s) => s.awaitingServer);
  const close = useInspection((s) => s.close);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!target) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent): void => {
      const node = panelRef.current;
      if (!node) return;
      if (!node.contains(e.target as Node)) close();
    };
    // Defer the mousedown binding so the click that opens the panel
    // doesn't immediately close it.
    const t = window.setTimeout(() => {
      window.addEventListener('mousedown', onDown);
    }, 0);
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [target, close]);

  if (!target || !data) return null;

  return (
    <div
      ref={panelRef}
      className={styles.panel}
      role="dialog"
      aria-label="Inspection"
      aria-live="polite"
    >
      <header className={styles.header}>
        <h3 className={styles.title}>{titleFor(data)}</h3>
        <button
          type="button"
          className={styles.closeButton}
          onClick={close}
          aria-label="Close inspection"
        >
          <X size={16} />
        </button>
      </header>
      {awaitingServer ? (
        <div className={styles.loading}>
          Awaiting server data…
        </div>
      ) : null}
      <div className={styles.body}>
        <InspectionBody data={data} />
      </div>
    </div>
  );
}

function InspectionBody({ data }: { data: InspectionData }): JSX.Element {
  if ('Tile' in data) return <><TileBody tile={data.Tile} /><WeatherSection /></>;
  if ('Prop' in data) return <><PropBody prop={data.Prop} /><WeatherSection /></>;
  if ('Building' in data) return <BuildingBody building={data.Building} />;
  if ('Avatar' in data) return <AvatarBody avatar={data.Avatar} />;
  return <div className={styles.rowValue}>Unknown inspection kind.</div>;
}

function TileBody({ tile }: { tile: TileInspection }): JSX.Element {
  const model = useWorldModel((s) => s.model);
  const [x, y] = tile.position;
  const idx = (x >= 0 && x < model.width && y >= 0 && y < model.height)
    ? tileIndex(x, y, model.width) : -1;
  const biome = idx >= 0 ? model.tileBiomes[idx] ?? 0 : 0;
  const moisture = idx >= 0 ? model.tileMoisture[idx] ?? 0 : 0;

  return (
    <dl className={styles.dl}>
      <Kv label="Position" value={`(${x}, ${y})`} />
      <Kv label="Material" value={materialLabel(tile.material)} />
      <Kv label="Biome" value={biomeLabel(biome)} />
      <Kv label="Elevation" value={`${tile.elevation} (${elevationBand(tile.elevation)})`} />
      <Kv label="Moisture" value={moistureLabel(moisture, model.moistureReceived)} />
      <Kv label="Indoors" value={tile.indoors ? 'Yes' : 'No'} />
      {tile.room !== null && (
        <Kv label="Room" value={`#${tile.room}`} />
      )}
    </dl>
  );
}

function PropBody({ prop }: { prop: PropInspection }): JSX.Element {
  const propEntry = useWorldModel((s) => s.model.props.get(prop.id));
  const visualState = propEntry?.visual_state ?? 0;
  const machineState = propEntry?.machine_state ?? null;
  const isSpoiling = machineState !== null && SPOILAGE_STAGES.has(machineState);
  const isCrop = !isSpoiling && (machineState !== null || visualState > 0);
  const freshness = freshnessLabel(machineState);

  return (
    <dl className={styles.dl}>
      <Kv label="Name" value={propLabel(prop.kind)} />
      <Kv label="ID" value={`#${prop.id}`} />
      <Kv label="Position" value={`(${prop.position[0]}, ${prop.position[1]})`} />
      {isCrop && (
        <>
          <Kv label="Stage" value={cropStageLabel(visualState)} />
          {machineState && (
            <Kv label="Crop" value={machineState} />
          )}
        </>
      )}
      {freshness !== null && (
        <Kv label="Freshness" value={freshness} />
      )}
      {prop.harvests_remaining !== null && (
        <Kv label="Harvests" value={String(prop.harvests_remaining)} />
      )}
    </dl>
  );
}

function WeatherSection(): JSX.Element {
  const weather = useWorldModel((s) => s.model.weather);
  const clock = useWorldModel((s) => s.model.clock);

  // wind_direction is in radians on the model — convert to degrees for the label function
  const windDeg = (weather.wind_direction * 180) / Math.PI;

  return (
    <div className={styles.modSection}>
      <div className={styles.modTitle}>Ambient</div>
      <dl className={styles.dl}>
        <Kv label="Weather" value={weather.kind} />
        <Kv label="Temp" value={`${Math.round(weather.temperature)}°C (feels ${Math.round(weather.apparent_temperature)}°C)`} />
        <Kv label="Humidity" value={`${Math.round(weather.humidity * 100)}%`} />
        <Kv label="Wind" value={`${windDirectionLabel(windDeg)} ${weather.wind_speed.toFixed(1)} m/s`} />
        <Kv label="Visibility" value={`${Math.round(weather.visibility * 100)}%`} />
        <Kv label="Time" value={`Day ${clock.day + 1}, ${clock.phase}, ${clock.season} Y${clock.year + 1}`} />
      </dl>
    </div>
  );
}

function BuildingBody({ building }: { building: BuildingInspection }): JSX.Element {
  return (
    <dl className={styles.dl}>
      <Kv label="Id" value={`#${building.id}`} />
      <Kv label="Archetype" value={building.archetype} />
      <Kv
        label="Centre"
        value={`${building.center[0]}, ${building.center[1]}`}
      />
      <Kv
        label="Owner"
        value={building.owner !== null ? `soma-${building.owner}` : 'unclaimed'}
      />
    </dl>
  );
}

function AvatarBody({ avatar }: { avatar: AvatarInspection }): JSX.Element {
  const playerId = 'Player' in avatar.entity ? avatar.entity.Player : null;
  const presence = usePresence((s) =>
    playerId !== null ? s.others.get(playerId)?.presence ?? null : null,
  );
  return (
    <>
      <dl className={styles.dl}>
        <Kv
          label="Name"
          value={avatar.display_name ?? entityLabel(avatar.entity)}
        />
        <Kv label="Entity" value={entityLabel(avatar.entity)} />
        <Kv
          label="Position"
          value={`${avatar.position[0]}, ${avatar.position[1]}`}
        />
        <Kv label="Action" value={avatar.action_status} />
        {avatar.role !== null ? <Kv label="Role" value={avatar.role} /> : null}
        {avatar.connection !== null ? (
          <Kv label="Connection" value={avatar.connection} />
        ) : null}
        {presence !== null ? (
          <>
            <dt className={styles.rowLabel}>Presence</dt>
            <dd className={styles.rowValue} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <PresenceDot presence={presence} size={8} ring="#313338" />
              {presence}
            </dd>
          </>
        ) : null}
      </dl>
      <ModerationActions avatar={avatar} />
    </>
  );
}

function ModerationActions({ avatar }: { avatar: AvatarInspection }): JSX.Element | null {
  const selfPlayerId = useWorldModel((s) => s.selfPlayerId);
  const players = useWorldModel((s) => s.model.players);
  const send = useLiveWire((s) => s.send);
  const pushToast = useToasts((s) => s.push);
  const closePanel = useInspection((s) => s.close);

  const [showConfirmKick, setShowConfirmKick] = useState(false);
  const [kickReason, setKickReason] = useState('');

  // Only Player entities are moderatable in Phase 1 (SOMAs don't have
  // roles yet). Extract the target PlayerId from the entity tag.
  const targetPlayerId: PlayerId | null = useMemo(() => {
    if ('Player' in avatar.entity) return avatar.entity.Player;
    return null;
  }, [avatar.entity]);
  const viewerPlayer =
    selfPlayerId !== null ? players.get(selfPlayerId) : undefined;
  const viewerRole = viewerPlayer?.role ?? 'Player';
  const targetRole = avatar.role ?? 'Player';
  const viewerIsSelf =
    targetPlayerId !== null && selfPlayerId === targetPlayerId;

  const ctx = { viewerRole, viewerIsSelf, targetRole };

  if (targetPlayerId === null || !hasAnyModerationAction(ctx)) {
    return null;
  }

  const dispatchKick = (): void => {
    const reason = kickReason.trim().slice(0, 200);
    if (!reason) return;
    // WorldIntent::Kick is authored by SERVER's P1.4 — the TS wire
    // shape casts through until the generated enum widens. Phase 1
    // offline has no server-side effect; the toast is immediate
    // local feedback.
    send({
      plane: 'World',
      intent: {
        Kick: { target: targetPlayerId, reason },
      } as never,
    });
    pushToast(
      'info',
      `Kick requested: ${avatar.display_name ?? `player-${targetPlayerId}`} — ${reason}`,
    );
    setShowConfirmKick(false);
    setKickReason('');
    closePanel();
  };

  const dispatchPromote = (): void => {
    send({
      plane: 'World',
      intent: {
        PromoteToModerator: { target: targetPlayerId },
      } as never,
    });
    pushToast('info', `Promoting ${avatar.display_name ?? `player-${targetPlayerId}`} → Moderator`);
    closePanel();
  };

  const dispatchDemote = (): void => {
    send({
      plane: 'World',
      intent: {
        DemoteToPlayer: { target: targetPlayerId },
      } as never,
    });
    pushToast('info', `Demoting ${avatar.display_name ?? `player-${targetPlayerId}`} → Player`);
    closePanel();
  };

  return (
    <section className={styles.modSection} aria-label="Moderation actions">
      <div className={styles.modTitle}>Moderation</div>
      {canKick(ctx) ? (
        showConfirmKick ? (
          <div className={styles.modConfirm}>
            <label className={styles.modLabel} htmlFor="kick-reason">
              Reason
            </label>
            <input
              id="kick-reason"
              type="text"
              value={kickReason}
              onChange={(e) => setKickReason(e.target.value)}
              maxLength={200}
              minLength={1}
              className={styles.modInput}
              placeholder="Required — 1 to 200 chars"
              autoFocus
            />
            <div className={styles.modConfirmRow}>
              <button
                type="button"
                className={styles.modCancelButton}
                onClick={() => {
                  setShowConfirmKick(false);
                  setKickReason('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.modDangerButton}
                onClick={dispatchKick}
                disabled={kickReason.trim().length === 0}
              >
                Confirm kick
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={styles.modDangerButton}
            onClick={() => setShowConfirmKick(true)}
          >
            <Ban size={14} />
            Kick…
          </button>
        )
      ) : null}
      {canPromote(ctx) ? (
        <button
          type="button"
          className={styles.modButton}
          onClick={dispatchPromote}
        >
          <ChevronUp size={14} />
          Promote to Moderator
        </button>
      ) : null}
      {canDemote(ctx) ? (
        <button
          type="button"
          className={styles.modButton}
          onClick={dispatchDemote}
        >
          <ChevronDown size={14} />
          Demote to Player
        </button>
      ) : null}
    </section>
  );
}

function Kv({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <>
      <dt className={styles.rowLabel}>{label}</dt>
      <dd className={styles.rowValue}>{value}</dd>
    </>
  );
}

function titleFor(data: InspectionData): string {
  if ('Tile' in data) return 'Tile';
  if ('Prop' in data) return propLabel(data.Prop.kind);
  if ('Building' in data) return 'Building';
  if ('Avatar' in data) return data.Avatar.display_name ?? 'Avatar';
  return 'Inspection';
}

function entityLabel(entity: AvatarInspection['entity']): string {
  if ('Player' in entity) return `player-${entity.Player}`;
  if ('Soma' in entity) return `soma-${entity.Soma}`;
  return '?';
}
